// 우측 슬라이드 패널 / 모바일 바텀시트 — CancelAgencyClients.js editPanel(데스크톱)·editSheet(모바일) 동작 그대로.
// props: open, title, subtitle, badge (타이틀 옆 노드), tabs:[{id,label,badge}], tab, onTab(id), onClose,
//        width: 720|520|440 (기본 520), footer (하단 고정 액션 — 없으면 푸터 영역 자체가 없다. 탭별로 바꿔도 안전),
//        children (본문, 스크롤), isMobile,
//        disableEsc (패널 **위에** 라이트박스 등 다른 오버레이가 떠 있는 동안 true — ESC 가 2겹으로 닫히는 것을 막는다.
//                    ✕·바깥 클릭은 그대로 두므로 onClose no-op 보다 덜 침습적이다.)
// 닫기 3경로: ✕ · ESC · 바깥(배경) 클릭. panelIn 은 마운트 16ms 뒤 true (마운트 프레임엔 transition 이 안 돈다).
// prefers-reduced-motion 이면 전이 없음. role="dialog" aria-label=title. 본문 scroll + 푸터 고정.
// 위에 CustomModal(확인/경고)이 떠 있을 때 ESC 가 패널까지 닫지 않게 하려면 호출자가 그 동안 onClose 를 no-op 으로 준다.
import React, { useState, useEffect } from 'react';
import { T, FONT } from '../theme';
import HeaderTabs from './HeaderTabs';

var reduceMotion = function () {
  return (typeof window !== 'undefined' && window.matchMedia) ? !!window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
};

// footer 영역은 '내용이 있을 때만' 그린다 (C2 — 탭마다 푸터가 달라져도 본문 높이가 45px 씩 튀지 않게).
// null/undefined/false 뿐 아니라 [] · [false, null] 처럼 **비어 있는 배열**도 푸터 없음으로 본다
// (탭별로 `footer={[canSave && <button/>]}` 처럼 조립하면 truthy 배열인데 그려지는 건 없는 상태가 된다).
function hasFooter(f) {
  if (!f) return false;
  if (Array.isArray(f)) return f.some(hasFooter);
  return true;
}

var xDesk = { border: '1px solid ' + T.bd, borderRadius: 8, background: T.card, fontSize: 16, color: T.muted, minWidth: 36, minHeight: 36, cursor: 'pointer', padding: 0, flexShrink: 0, marginLeft: 'auto' };
var xMob = { border: 'none', background: 'none', fontSize: 18, color: T.faint, minWidth: 40, minHeight: 40, cursor: 'pointer', padding: 0, flexShrink: 0, marginLeft: 'auto' };

function SlidePanel(props) {
  var open = !!props.open, onClose = props.onClose, isMobile = !!props.isMobile;
  // 배열 구조분해 — eslint react-hooks 가 setter 를 stable 로 인식해야 exhaustive-deps 경고가 안 난다
  // (st[1] 형태면 '누락 의존성 setPanelIn' 경고. 동작은 동일).
  var [panelIn, setPanelIn] = useState(false);
  var noMotion = reduceMotion();

  useEffect(function () {
    if (!open) { setPanelIn(false); return undefined; }
    var t = setTimeout(function () { setPanelIn(true); }, 16);
    return function () { clearTimeout(t); };
  }, [open]);

  useEffect(function () {
    if (!open || props.disableEsc) return undefined;
    var on = function (e) { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', on);
    return function () { window.removeEventListener('keydown', on); };
  }, [open, onClose, props.disableEsc]);

  if (!open) return null;

  var tabItems = (props.tabs || []).map(function (t) {
    return { id: t.id, label: t.label, badge: t.badge, on: t.id === props.tab, onClick: function () { if (props.onTab) props.onTab(t.id); } };
  });
  var head = (<div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: isMobile ? 15 : FONT.title, fontWeight: 800, color: T.navy, letterSpacing: '-.3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{props.title}</div>
      {props.subtitle ? <div style={{ fontSize: isMobile ? 12 : FONT.body, color: T.muted, marginTop: 2, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>{props.subtitle}</div> : null}
    </div>
    {props.badge ? <span style={{ marginLeft: 6, flexShrink: 0 }}>{props.badge}</span> : null}
    <button type="button" onClick={onClose} title="닫기" aria-label="패널 닫기" style={isMobile ? xMob : xDesk}>✕</button>
  </div>);
  var tabs = tabItems.length ? <div style={{ display: 'flex' }}><HeaderTabs items={tabItems} isMobile={isMobile} /></div> : null;

  if (isMobile) {
    return (<div className="v2-panel" data-testid="v2-panel-backdrop" onClick={onClose}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9998, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <div role="dialog" aria-label={typeof props.title === 'string' ? props.title : undefined} onClick={function (e) { e.stopPropagation(); }}
        style={{ background: T.card, borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '92vh', display: 'flex', flexDirection: 'column',
          boxShadow: T.shadowSheet, transform: panelIn ? 'translateY(0)' : 'translateY(100%)', transition: noMotion ? 'none' : 'transform .22s ease' }}>
        <div style={{ padding: '14px 14px 0', borderBottom: tabs ? '1px solid ' + T.bd : 'none' }}>{head}{tabs}</div>
        <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '10px 14px 28px' }}>{props.children}</div>
        {hasFooter(props.footer) ? <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '12px 14px', borderTop: '1px solid ' + T.bd, background: T.head }}>{props.footer}</div> : null}
      </div>
    </div>);
  }

  var w = props.width || 520;
  return (<div className="v2-panel" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998 }}>
    <div data-testid="v2-panel-backdrop" onClick={onClose}
      style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.28)', opacity: panelIn ? 1 : 0, transition: noMotion ? 'none' : 'opacity .22s ease' }} />
    <div role="dialog" aria-label={typeof props.title === 'string' ? props.title : undefined}
      style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(' + w + 'px, 94vw)', background: T.card, boxShadow: T.shadowPanel,
        display: 'flex', flexDirection: 'column', transform: panelIn ? 'translateX(0)' : 'translateX(100%)', transition: noMotion ? 'none' : 'transform .22s ease' }}>
      <div style={{ padding: tabs ? '16px 22px 0' : '18px 22px 16px', borderBottom: '1px solid ' + T.bd }}>{head}{tabs}</div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '6px 22px 22px' }}>{props.children}</div>
      {hasFooter(props.footer) ? <div style={{ borderTop: '1px solid ' + T.bd, padding: '12px 22px', background: T.head, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>{props.footer}</div> : null}
    </div>
  </div>);
}

export default SlidePanel;
